import { ReceiveShipmentsService } from '@/api';
import type { ApiError, GetDeliveryDetailsResponse } from '@/api/receive';
import type { RootState } from '@/app/rootReducer';
import { DeliveryStatus } from '@/pages/ManageDelivery/PageManageDelivery/types';
import { EpcStatusValues } from '@/types/enum';
import type { ManageDeliveryInitialState } from '@/types/partialDeliveriesManage';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

const initialState: ManageDeliveryInitialState = {
  checkedEpcs: [],
  searchedStoreCode: '',
  searchedDeliveryCode: '',
  searchedStoreCodes: [],
  searchedDeliveryCodes: [],
  deliveryStatuses: ['In Use', 'Partial'],
};

export const fetchDeliveryDetails = createAsyncThunk<
  Record<string, GetDeliveryDetailsResponse> | undefined,
  { deliveryNumber: string[]; storeCode: string },
  { state: RootState; rejectValue: ApiError }
>(
  'manageDelivery/fetchDeliveryDetails',
  async ({ deliveryNumber, storeCode }, { rejectWithValue }) => {
    try {
      const response = await ReceiveShipmentsService.receiveshipDeliveryDetails(
        {
          deliveryNumber,
          storeCode,
          flowType: 'PDM'
        }
      );

      if (response) {
        return response;
      }

      return undefined;
    } catch (err) {
      return rejectWithValue(err as ApiError);
    }
  }
);

const manageDeliverySlice = createSlice({
  name: 'manageDelivery',
  initialState,
  reducers: {
    setSearchedStoreCode: (state, { payload }: PayloadAction<string>) => {
      state.searchedStoreCode = payload;
    },
    setSearchedDeliveryCode: (state, { payload }: PayloadAction<string>) => {
      state.searchedDeliveryCode = payload;
    },
    setSearchedStoreCodes: (state, { payload }: PayloadAction<string[]>) => {
      state.searchedStoreCodes = payload;
    },
    setSearchedDeliveryCodes: (state, { payload }: PayloadAction<string[]>) => {
      state.searchedDeliveryCodes = payload;
    },
    setDeliveryStatuses: (
      state,
      { payload }: PayloadAction<DeliveryStatus>
    ) => {
      const statusIndex = state.deliveryStatuses.findIndex(
        (s: string) => s === payload
      );

      if (statusIndex !== -1) {
        state.deliveryStatuses.splice(statusIndex, 1);
      } else {
        state.deliveryStatuses.push(payload);
      }
    },
    setManagedDeliveryInUse: (
      state,
      { payload }: PayloadAction<string | undefined>
    ) => {
      state.deliveryManagedInUse = payload;
    },
    setManagedDeliveriesInUse: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.deliveriesManagedInUse = payload;
    },
    setShouldFetchDeliveries: (state, { payload }: PayloadAction<boolean>) => {
      state.shouldFetchDeliveries = payload;
    },
    checkEpcs: (
      state,
      { payload }: PayloadAction<{ deliveryNumber: string; epcCode: string }[]>
    ) => {
      state.checkedEpcs = payload;
    },
    checkEpc: (
      state,
      { payload }: PayloadAction<{ deliveryNumber: string; epcCode: string }>
    ) => {
      const epcIndex = state.checkedEpcs.findIndex(
        e =>
          e.deliveryNumber === payload.deliveryNumber &&
          e.epcCode === payload.epcCode
      );

      if (epcIndex !== -1) {
        state.checkedEpcs.splice(epcIndex, 1);
      } else {
        state.checkedEpcs.push(payload);
      }
    },
    changeEpcStatus: (
      state,
      {
        payload,
      }: PayloadAction<{
        epcs: { deliveryNumber: string; epcCode: string }[];
        status: EpcStatusValues;
      }>
    ) => {
      const { epcs, status } = payload;

      if (state.deliveryDetails) {
        for (const { deliveryNumber, epcCode } of epcs) {
          const delivery = state.deliveryDetails[deliveryNumber];
          if (!delivery?.epcsInTransit) continue;

          const epcIndex = delivery.epcsInTransit.findIndex(
            e => e.epcCode === epcCode
          );

          if (epcIndex !== -1) {
            delivery.epcsInTransit[epcIndex].lastEpcStatus = status;
          }
        }
      }
    },
    initCheckedEpcs: state => {
      state.checkedEpcs = [];
    },
    initManageDeliveryState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDeliveryDetails.pending, state => {
        state.fetchDeliveryDetailsHasError = undefined;
        state.fetchDeliveryDetailsIsLoading = true;
      })
      .addCase(fetchDeliveryDetails.fulfilled, (state, { payload }) => {
        state.deliveryDetails = payload;
        state.fetchDeliveryDetailsIsLoading = false;
      })
      .addCase(fetchDeliveryDetails.rejected, (state, { payload }) => {
        state.fetchDeliveryDetailsIsLoading = false;
        state.fetchDeliveryDetailsHasError = payload as ApiError;
      });
  },
});

export const {
  setSearchedStoreCode,
  setSearchedStoreCodes,
  setSearchedDeliveryCode,
  setSearchedDeliveryCodes,
  setShouldFetchDeliveries,
  setDeliveryStatuses,
  setManagedDeliveryInUse,
  setManagedDeliveriesInUse,
  checkEpcs,
  checkEpc,
  changeEpcStatus,
  initCheckedEpcs,
  initManageDeliveryState,
} = manageDeliverySlice.actions;
export default manageDeliverySlice.reducer;
